import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Layout } from '@/components/layout'
import { useAuditLogs, useAuditActions, useExportAuditLogs } from '@/api/audit-logs'
import { useUsers } from '@/api/users'
import { useCrises } from '@/api/crisis'
import {
  FileText,
  Search,
  Download,
  RefreshCw,
  Eye,
  Calendar,
  User,
  Shield,
  Activity,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { nl } from 'date-fns/locale'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { useToast } from '@/lib/hooks/useToast'
import { useHasPermission } from '@/lib/hooks/usePermission'
import type { AuditLog } from '@/lib/types'

export function AdminAuditLogsPage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)

  // Permission checks
  const canExportAudit = useHasPermission('audit.export')

  const perPage = 20

  // Filter states
  const [filters, setFilters] = useState({
    user_id: '',
    crisis_id: '',
    action: 'all',
    resource_type: 'all',
    start_date: '',
    end_date: '',
  })

  const {
    data: logsData,
    isLoading,
    refetch,
  } = useAuditLogs(currentPage, perPage, {
    ...Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== '' && value !== 'all')
    ),
  })

  const { data: actionsData } = useAuditActions()
  const { mutateAsync: exportLogs, isPending: isExporting } = useExportAuditLogs()
  const { data: usersData } = useUsers()
  const { data: crisesData } = useCrises()

  // Filter logs by search query
  const filteredLogs = logsData?.logs.filter((log: AuditLog) => {
    const query = searchQuery.toLowerCase()
    return (
      log.action.toLowerCase().includes(query) ||
      (log.description && log.description.toLowerCase().includes(query)) ||
      (log.resourceType && log.resourceType.toLowerCase().includes(query)) ||
      log.userId.toLowerCase().includes(query)
    )
  })

  const getActionBadgeColor = (action: string) => {
    // Color code based on action type
    if (action.includes('created')) return 'bg-green-100 text-green-800'
    if (action.includes('updated')) return 'bg-blue-100 text-blue-800'
    if (action.includes('deleted')) return 'bg-red-100 text-red-800'
    if (action.includes('login')) return 'bg-purple-100 text-purple-800'
    if (action.includes('logout')) return 'bg-gray-100 text-gray-800'
    return 'bg-gray-100 text-gray-800'
  }

  const getResourceTypeIcon = (resourceType: string | null) => {
    switch (resourceType?.toLowerCase()) {
      case 'user':
        return <User className="h-4 w-4" />
      case 'crisis':
        return <Shield className="h-4 w-4" />
      case 'scan':
        return <Activity className="h-4 w-4" />
      default:
        return <FileText className="h-4 w-4" />
    }
  }

  const handleViewClick = (log: AuditLog) => {
    setSelectedLog(log)
    setIsViewDialogOpen(true)
  }

  const handleExportClick = () => {
    setIsExportDialogOpen(true)
  }

  const handleExportSubmit = async () => {
    try {
      const blob = await exportLogs({
        ...Object.fromEntries(
          Object.entries(filters).filter(([, value]) => value !== '' && value !== 'all')
        ),
      })

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit_logs_export_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      setIsExportDialogOpen(false)
      toast({
        title: t('admin.audit.exportSuccess'),
        description: t('admin.audit.exportSuccessDesc'),
      })
    } catch (error) {
      toast({
        title: t('admin.audit.exportError'),
        description: error instanceof Error ? error.message : t('admin.audit.unknownError'),
        variant: 'destructive',
      })
    }
  }

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setCurrentPage(1) // Reset to first page when filters change
  }

  const clearFilters = () => {
    setFilters({
      user_id: '',
      crisis_id: '',
      action: 'all',
      resource_type: 'all',
      start_date: '',
      end_date: '',
    })
    setCurrentPage(1)
  }

  const totalPages = logsData ? Math.ceil(logsData.total / perPage) : 1

  return (
    <Layout>
      {/* Header */}
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground">
                {t('admin.audit.title')}
              </h1>
              <p className="text-muted-foreground mt-2">{t('admin.audit.description')}</p>
            </div>
            <div className="flex space-x-2">
              <Button onClick={() => refetch()} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('admin.vernieuwen')}
              </Button>
              {canExportAudit && (
                <Button onClick={handleExportClick} size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  {t('admin.audit.export')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative bg-white">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('admin.audit.searchPlaceholder')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Search className="h-5 w-5" />
              <span>{t('admin.audit.filters')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="user_id">{t('admin.audit.user')}</Label>
                <Select
                  value={filters.user_id}
                  onValueChange={value => handleFilterChange('user_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('admin.audit.selectUser')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.audit.allUsers')}</SelectItem>
                    {usersData?.users?.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.displayName || `${user.firstName} ${user.lastName}`} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="crisis_id">{t('admin.audit.crisis')}</Label>
                <Select
                  value={filters.crisis_id}
                  onValueChange={value => handleFilterChange('crisis_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('admin.audit.selectCrisis')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.audit.allCrises')}</SelectItem>
                    {crisesData?.data?.map(crisis => (
                      <SelectItem key={crisis.id} value={crisis.id}>
                        {crisis.type} - {crisis.location} (
                        {new Date(crisis.createdAt || '').toLocaleDateString('nl-NL')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="action">{t('admin.audit.action')}</Label>
                <Select
                  value={filters.action}
                  onValueChange={value => handleFilterChange('action', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('admin.audit.selectAction')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.audit.allActions')}</SelectItem>
                    {actionsData?.actions.map(action => (
                      <SelectItem key={action} value={action}>
                        {action.replace(/_/g, ' ').toLowerCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="resource_type">{t('admin.audit.resourceType')}</Label>
                <Select
                  value={filters.resource_type}
                  onValueChange={value => handleFilterChange('resource_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('admin.audit.selectResourceType')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.audit.allResourceTypes')}</SelectItem>
                    <SelectItem value="User">{t('admin.audit.resourceUser')}</SelectItem>
                    <SelectItem value="Crisis">{t('admin.audit.resourceCrisis')}</SelectItem>
                    <SelectItem value="Scan">{t('admin.audit.resourceScan')}</SelectItem>
                    <SelectItem value="Task">{t('admin.audit.resourceTask')}</SelectItem>
                    <SelectItem value="File">{t('admin.audit.resourceFile')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="start_date">{t('admin.audit.startDate')}</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={filters.start_date}
                  onChange={e => handleFilterChange('start_date', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="end_date">{t('admin.audit.endDate')}</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={filters.end_date}
                  onChange={e => handleFilterChange('end_date', e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <Button variant="outline" onClick={clearFilters}>
                {t('admin.audit.clearFilters')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('admin.audit.totalLogs')}</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{logsData?.total || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('admin.audit.todayLogs')}</CardTitle>
              <Calendar className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {logsData?.logs.filter((log: AuditLog) => {
                  const logDate = new Date(log.createdAt)
                  const today = new Date()
                  return logDate.toDateString() === today.toDateString()
                }).length || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('admin.audit.userActions')}</CardTitle>
              <User className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {logsData?.logs.filter((log: AuditLog) => log.action.includes('user_')).length || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t('admin.audit.systemActions')}
              </CardTitle>
              <Shield className="h-4 w-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {logsData?.logs.filter(
                  (log: AuditLog) => log.action.includes('login') || log.action.includes('logout')
                ).length || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Audit Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FileText className="h-5 w-5" />
              <span>{t('admin.audit.auditLogs')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin mr-2" />
                {t('admin.audit.loading')}
              </div>
            ) : filteredLogs && filteredLogs.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <FileText className="h-6 w-6 text-muted-foreground mr-2" />
                {t('admin.audit.noLogs')}
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {filteredLogs?.map((log: AuditLog) => (
                    <div
                      key={log.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="flex items-center space-x-2">
                          {getResourceTypeIcon(log.resourceType)}
                          <Badge className={getActionBadgeColor(log.action)}>
                            {log.action.replace(/_/g, ' ').toLowerCase()}
                          </Badge>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">
                            {log.description || log.action.replace(/_/g, ' ').toLowerCase()}
                          </p>
                          <div className="flex items-center space-x-4 mt-1 text-sm text-muted-foreground">
                            <span>
                              {t('admin.audit.user')}: {log.userId}
                            </span>
                            {log.crisisId && (
                              <span>
                                {t('admin.audit.crisis')}: {log.crisisId}
                              </span>
                            )}
                            {log.resourceType && log.resourceId && (
                              <span>
                                {log.resourceType}: {log.resourceId}
                              </span>
                            )}
                            <span>
                              {formatDistanceToNow(new Date(log.createdAt), {
                                addSuffix: true,
                                locale: nl,
                              })}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Button variant="ghost" size="sm" onClick={() => handleViewClick(log)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-6 border-t">
                    <p className="text-sm text-muted-foreground">
                      {t('admin.audit.showing')} {(currentPage - 1) * perPage + 1} -{' '}
                      {Math.min(currentPage * perPage, logsData?.total || 0)} {t('admin.audit.of')}{' '}
                      {logsData?.total || 0}
                    </p>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        {t('admin.audit.previous')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        {t('admin.audit.next')}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View Log Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('admin.audit.logDetails')}</DialogTitle>
            <DialogDescription>{t('admin.audit.logDetailsDescription')}</DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.action')}</Label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Badge className={getActionBadgeColor(selectedLog.action)}>
                      {selectedLog.action.replace(/_/g, ' ').toLowerCase()}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.timestamp')}</Label>
                  <p className="font-medium">
                    {formatDistanceToNow(new Date(selectedLog.createdAt), {
                      addSuffix: true,
                      locale: nl,
                    })}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(selectedLog.createdAt).toLocaleString('nl-NL')}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.userId')}</Label>
                  <p className="font-medium font-mono text-sm">{selectedLog.userId}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.crisisId')}</Label>
                  <p className="font-medium font-mono text-sm">{selectedLog.crisisId || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.resourceType')}</Label>
                  <p className="font-medium">{selectedLog.resourceType || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.resourceId')}</Label>
                  <p className="font-medium font-mono text-sm">{selectedLog.resourceId || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.ipAddress')}</Label>
                  <p className="font-medium font-mono text-sm">{selectedLog.ipAddress || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.requestMethod')}</Label>
                  <p className="font-medium">{selectedLog.requestMethod || '-'}</p>
                </div>
              </div>

              {selectedLog.description && (
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.description')}</Label>
                  <p className="mt-1">{selectedLog.description}</p>
                </div>
              )}

              {selectedLog.requestPath && (
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.requestPath')}</Label>
                  <p className="font-mono text-sm mt-1">{selectedLog.requestPath}</p>
                </div>
              )}

              {selectedLog.userAgent && (
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.userAgent')}</Label>
                  <p className="text-sm mt-1 break-all">{selectedLog.userAgent}</p>
                </div>
              )}

              {selectedLog.details && (
                <div>
                  <Label className="text-muted-foreground">{t('admin.audit.details')}</Label>
                  <pre className="mt-1 p-2 bg-muted rounded text-sm overflow-x-auto">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              {t('admin.audit.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.audit.exportLogs')}</DialogTitle>
            <DialogDescription>{t('admin.audit.exportLogsDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('admin.audit.exportCurrentFilters')}</p>
            {Object.entries(filters).some(([, value]) => value && value !== 'all') && (
              <div className="p-3 bg-muted rounded">
                <p className="text-sm font-medium mb-2">{t('admin.audit.activeFilters')}:</p>
                <div className="space-y-1">
                  {Object.entries(filters)
                    .filter(([, value]) => value && value !== 'all')
                    .map(([key, value]) => (
                      <div key={key} className="text-sm">
                        <span className="font-medium">{key}:</span> {value}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsExportDialogOpen(false)}
              disabled={isExporting}
            >
              {t('admin.audit.cancel')}
            </Button>
            <Button onClick={handleExportSubmit} disabled={isExporting}>
              {isExporting ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {t('admin.audit.export')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  )
}
